import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Autocomplete,
  Divider,
  Tooltip,
  useTheme,
  Snackbar,
  Alert,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Group as GroupIcon,
  ExpandMore as ExpandMoreIcon,
  DateRange as DateRangeIcon,
  BusinessCenter as BusinessCenterIcon,
  Category as CategoryIcon,
  Campaign as CampaignIcon,
  Bookmark as BookmarkIcon,
  Save as SaveIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import {
  Theme
} from '@mui/material/styles';
import SidebarLayout from '../../components/layout/SidebarLayout';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import ptBR from 'date-fns/locale/pt-BR';
import { API_BASE_URL } from '../../config';

// Interface for cliente
interface Cliente {
  id: number;
  nome: string;
  descricao?: string;
  ativo: boolean;
  dataremocao?: string;
  idmongodb?: string;
  related_periodos?: PeriodoSimples[];
}

// Interface for detailed cliente with periodos
interface ClienteDetail extends Cliente {
  periodos_info: PeriodoInfo[];
}

// Interface for simple periodo
interface PeriodoSimples {
  id: number;
  inicio: string;
  fim: string;
}

// Interface for detailed periodo info
interface PeriodoInfo {
  id: number;
  inicio: string;
  fim: string;
  marcas_ids: number[];
  itens_ids: number[];
  setores_ids: number[];
  emissoras_ids: number[];
}

// Interface for periodo access in create/update operations
interface PeriodoAcesso {
  inicio: string;
  fim: string;
  marcas_ids: number[];
  itens_ids: number[];
  setores_ids: number[];
  emissoras_ids: number[];
}

// Interface for cliente creation
interface ClienteCreate {
  nome: string;
  descricao?: string;
  ativo?: boolean;
  periodos_acesso: PeriodoAcesso[];
}

// Interface for cliente update
interface ClienteUpdate {
  nome?: string;
  descricao?: string;
  ativo?: boolean;
  periodos_acesso?: PeriodoAcesso[];
}

// Interface for cliente filter
interface ClienteFilter {
  nome?: string;
  ativo?: boolean;
}

// Interface for reference entities
interface EntidadeSimples {
  id: number;
  nome: string;
}

// Mock service for clientes - In a real app, this would be a separate service file
const ClientesService = {
  // Base URL for API calls
  baseUrl: API_BASE_URL.replace('/api/v1', ''),
  
  getClientes: async (filters: ClienteFilter, skip: number, limit: number): Promise<Cliente[]> => {
    const queryParams = new URLSearchParams();
    
    queryParams.append('skip', skip.toString());
    queryParams.append('limit', limit.toString());
    
    if (filters.nome) {
      queryParams.append('nome', filters.nome);
    }
    
    if (filters.ativo !== undefined) {
      queryParams.append('ativo', filters.ativo.toString());
    }
    
    try {
      const url = `${ClientesService.baseUrl}/api/v1/clientes?${queryParams.toString()}`;
      console.log(`Fetching clients from: ${url}`);
      
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json'
        }
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Error response: Status ${response.status}`, errorText);
        throw new Error(`Erro ao buscar clientes: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching clients:', error);
      // Return empty array to prevent UI crashes
      return [];
    }
  },
  
  getClientesCount: async (filters: ClienteFilter): Promise<number> => {
    const queryParams = new URLSearchParams();
    
    if (filters.nome) {
      queryParams.append('nome', filters.nome);
    }
    
    if (filters.ativo !== undefined) {
      queryParams.append('ativo', filters.ativo.toString());
    }
    
    try {
      const url = `${ClientesService.baseUrl}/api/v1/clientes/count?${queryParams.toString()}`;
      console.log(`Fetching clients count from: ${url}`);
      
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json'
        }
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Error response: Status ${response.status}`, errorText);
        throw new Error(`Erro ao contar clientes: ${response.status} ${response.statusText}`);
      }
      
      const count = await response.json();
      return count;
    } catch (error) {
      console.error('Error fetching client count:', error);
      // Return 0 to prevent UI crashes
      return 0;
    }
  },
  
  getCliente: async (id: number): Promise<Cliente> => {
    try {
      const url = `${ClientesService.baseUrl}/api/v1/clientes/${id}`;
      console.log(`Fetching client details from: ${url}`);
      
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json'
        }
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Error response: Status ${response.status}`, errorText);
        throw new Error(`Erro ao buscar cliente: ${response.status} ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error fetching client ${id}:`, error);
      throw error;
    }
  },
  
  getClienteDetail: async (id: number): Promise<ClienteDetail> => {
    try {
      const url = `${ClientesService.baseUrl}/api/v1/clientes/${id}/detail`;
      console.log(`Fetching client detailed info from: ${url}`);
      
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json'
        }
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Error response: Status ${response.status}`, errorText);
        
        // Se o erro for 500, provavelmente é o problema de schema do banco
        if (response.status === 500) {
          throw new Error(`Erro no servidor ao buscar detalhes do cliente. Possível problema com o schema do banco de dados.`);
        }
        
        throw new Error(`Erro ao buscar detalhes do cliente: ${response.status} ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error fetching client details ${id}:`, error);
      throw error;
    }
  },
  
  createCliente: async (cliente: ClienteCreate): Promise<ClienteDetail> => {
    try {
      const url = `${ClientesService.baseUrl}/api/v1/clientes`;
      console.log(`Creating client at: ${url}`, cliente);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(cliente),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Error response: Status ${response.status}`, errorText);
        throw new Error(`Erro ao criar cliente: ${response.status} ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error creating client:', error);
      throw error;
    }
  },
  
  updateCliente: async (id: number, cliente: ClienteUpdate): Promise<ClienteDetail> => {
    try {
      const url = `${ClientesService.baseUrl}/api/v1/clientes/${id}`;
      console.log(`Updating client at: ${url}`, cliente);
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(cliente),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Error response: Status ${response.status}`, errorText);
        throw new Error(`Erro ao atualizar cliente: ${response.status} ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error updating client ${id}:`, error);
      throw error;
    }
  },
  
  deleteCliente: async (id: number): Promise<Cliente> => {
    try {
      const url = `${ClientesService.baseUrl}/api/v1/clientes/${id}`;
      console.log(`Deleting client at: ${url}`);
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Error response: Status ${response.status}`, errorText);
        throw new Error(`Erro ao excluir cliente: ${response.status} ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error deleting client ${id}:`, error);
      throw error;
    }
  },
  
  getAllClientes: async (): Promise<Cliente[]> => {
    try {
      const url = `${ClientesService.baseUrl}/api/v1/clientes/all/list`;
      console.log(`Fetching all clients from: ${url}`);
      
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json'
        }
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Error response: Status ${response.status}`, errorText);
        throw new Error(`Erro ao buscar lista de clientes: ${response.status} ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching all clients:', error);
      // Return empty array to prevent UI crashes
      return [];
    }
  },
};

// Mock services for reference entities
const MarcasService = {
  getMarcas: async (): Promise<EntidadeSimples[]> => {
    try {
      const url = `${ClientesService.baseUrl}/api/v1/marcas/all/list`;
      console.log(`Fetching marcas from: ${url}`);
      
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Error response: Status ${response.status}`, errorText);
        throw new Error(`Erro ao buscar marcas: ${response.status} ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching marcas:', error);
      // Return empty array to prevent UI crashes
      return [];
    }
  }
};

const ItensService = {
  getItens: async (): Promise<EntidadeSimples[]> => {
    try {
      const url = `${ClientesService.baseUrl}/api/v1/itens/all/list`;
      console.log(`Fetching itens from: ${url}`);
      
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Error response: Status ${response.status}`, errorText);
        throw new Error(`Erro ao buscar itens: ${response.status} ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching itens:', error);
      // Return empty array to prevent UI crashes
      return [];
    }
  }
};

const SetoresService = {
  getSetores: async (): Promise<EntidadeSimples[]> => {
    try {
      const url = `${ClientesService.baseUrl}/api/v1/setores/all/list`;
      console.log(`Fetching setores from: ${url}`);
      
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Error response: Status ${response.status}`, errorText);
        throw new Error(`Erro ao buscar setores: ${response.status} ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching setores:', error);
      // Return empty array to prevent UI crashes
      return [];
    }
  }
};

const EmissorasService = {
  getEmissoras: async (search?: string, page: number = 0, limit: number = 100): Promise<EntidadeSimples[]> => {
    try {
      const queryParams = new URLSearchParams();
      
      // Add pagination parameters
      queryParams.append('skip', (page * limit).toString());
      queryParams.append('limit', limit.toString());
      
      // Add search parameter if provided
      if (search && search.trim()) {
        queryParams.append('nome', search.trim());
      }
      
      const url = `${ClientesService.baseUrl}/api/v1/emissoras?${queryParams.toString()}`;
      console.log(`Fetching emissoras from: ${url}`);
      
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Error response: Status ${response.status}`, errorText);
        throw new Error(`Erro ao buscar emissoras: ${response.status} ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching emissoras:', error);
      // Return empty array to prevent UI crashes
      return [];
    }
  },
  
  getAllEmissoras: async (): Promise<EntidadeSimples[]> => {
    try {
      let allEmissoras: EntidadeSimples[] = [];
      let page = 0;
      const limit = 500; // Fetch in larger batches for complete list
      let results: EntidadeSimples[] = [];
      
      do {
        results = await EmissorasService.getEmissoras(undefined, page, limit);
        allEmissoras = [...allEmissoras, ...results];
        page++;
      } while (results.length === limit);
      
      // Sort emissoras by name for better display
      allEmissoras.sort((a, b) => a.nome.localeCompare(b.nome));
      
      return allEmissoras;
    } catch (error) {
      console.error('Error fetching all emissoras:', error);
      return [];
    }
  }
};

const ClientesPage: React.FC = () => {
  const theme = useTheme();
  
  // Estado de listagem
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Estado de referências
  const [marcas, setMarcas] = useState<EntidadeSimples[]>([]);
  const [itens, setItens] = useState<EntidadeSimples[]>([]);
  const [setores, setSetores] = useState<EntidadeSimples[]>([]);
  const [emissoras, setEmissoras] = useState<EntidadeSimples[]>([]);
  const [loadingReferencias, setLoadingReferencias] = useState(false);

  // Estado de filtros
  const [filter, setFilter] = useState<ClienteFilter>({});
  
  // Estado de formulários
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentCliente, setCurrentCliente] = useState<Cliente | null>(null);
  const [currentClienteDetail, setCurrentClienteDetail] = useState<ClienteDetail | null>(null);
  const [newCliente, setNewCliente] = useState<ClienteCreate>({ 
    nome: '', 
    ativo: true,
    periodos_acesso: []
  });
  const [editCliente, setEditCliente] = useState<ClienteUpdate>({});

  // Estado para controle de periodos
  const [tabValue, setTabValue] = useState(0);
  const [periodosEditing, setPeriodosEditing] = useState<PeriodoAcesso[]>([]);

  // Estado de mensagens
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  // Adicionar ref para debounce
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Carregar todas as referências necessárias
  const carregarReferencias = useCallback(async () => {
    try {
      setLoadingReferencias(true);
      
      // Carregar marcas, itens, setores e emissoras
      const [marcasData, itensData, setoresData, emissorasData] = await Promise.all([
        MarcasService.getMarcas(),
        ItensService.getItens(),
        SetoresService.getSetores(),
        EmissorasService.getAllEmissoras() // Use the new method to get all emissoras
      ]);
      
      setMarcas(marcasData);
      setItens(itensData);
      setSetores(setoresData);
      setEmissoras(emissorasData);
    } catch (error) {
      console.error('Erro ao carregar referências:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao carregar dados de referência',
        severity: 'error',
      });
    } finally {
      setLoadingReferencias(false);
    }
  }, []);

  const fetchClientes = useCallback(async () => {
    try {
      setLoading(true);
      const skip = page * rowsPerPage;
      const limit = rowsPerPage;
      
      const data = await ClientesService.getClientes(filter, skip, limit);
      setClientes(data);
      
      // Obter contagem total para paginação
      const count = await ClientesService.getClientesCount(filter);
      setTotalCount(count);
    } catch (error) {
      console.error('Erro ao buscar clientes:', error);
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Erro ao buscar clientes',
        severity: 'error',
      });
      // Set default values to prevent UI crashes
      setClientes([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filter]);

  useEffect(() => {
    // Buscar clientes ao iniciar a página ou mudar filtros/paginação
    fetchClientes();
    // Carregar referências
    carregarReferencias();
  }, [fetchClientes, carregarReferencias]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field: keyof ClienteFilter, value: string | boolean | undefined) => {
    // Limpar timeout anterior se existir
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Atualizar o filtro imediatamente na UI
    setFilter((prev) => ({
      ...prev,
      [field]: value === '' ? undefined : value,
    }));

    // Debounce: aguardar 500ms antes de aplicar a busca
    searchTimeoutRef.current = setTimeout(() => {
      setPage(0); // Resetar paginação ao mudar filtro
    }, 500);
  };

  // Função para limpar filtro de busca
  const clearSearchFilter = () => {
    setFilter((prev) => ({
      ...prev,
      nome: undefined
    }));
    setPage(0);
  };

  // Funções para manipulação de períodos
  const handleAddPeriodo = () => {
    const newPeriodo: PeriodoAcesso = {
      inicio: new Date().toISOString().split('T')[0],
      fim: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
      marcas_ids: [],
      itens_ids: [],
      setores_ids: [],
      emissoras_ids: []
    };

    if (openCreateDialog) {
      setNewCliente(prev => ({
        ...prev,
        periodos_acesso: [...prev.periodos_acesso, newPeriodo]
      }));
    } else if (openEditDialog) {
      setPeriodosEditing(prev => [...prev, newPeriodo]);
    }
  };

  const handleRemovePeriodo = (index: number) => {
    if (openCreateDialog) {
      setNewCliente(prev => ({
        ...prev,
        periodos_acesso: prev.periodos_acesso.filter((_, i) => i !== index)
      }));
    } else if (openEditDialog) {
      setPeriodosEditing(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handlePeriodoChange = (index: number, field: keyof PeriodoAcesso, value: any) => {
    if (openCreateDialog) {
      setNewCliente(prev => {
        const periodos = [...prev.periodos_acesso];
        periodos[index] = { ...periodos[index], [field]: value };
        return { ...prev, periodos_acesso: periodos };
      });
    } else if (openEditDialog) {
      setPeriodosEditing(prev => {
        const periodos = [...prev];
        periodos[index] = { ...periodos[index], [field]: value };
        return periodos;
      });
    }
  };

  const handleEntitySelectionChange = (index: number, field: keyof PeriodoAcesso, newValue: number[]) => {
    if (openCreateDialog) {
      setNewCliente(prev => {
        const periodos = [...prev.periodos_acesso];
        periodos[index] = { ...periodos[index], [field]: newValue };
        return { ...prev, periodos_acesso: periodos };
      });
    } else if (openEditDialog) {
      setPeriodosEditing(prev => {
        const periodos = [...prev];
        periodos[index] = { ...periodos[index], [field]: newValue };
        return periodos;
      });
    }
  };

  const handleCreateCliente = async () => {
    try {
      // Verificar campos obrigatórios
      if (!newCliente.nome) {
        setSnackbar({
          open: true,
          message: 'Nome do cliente é obrigatório',
          severity: 'error',
        });
        return;
      }

      const result = await ClientesService.createCliente(newCliente);
      setOpenCreateDialog(false);
      setSnackbar({
        open: true,
        message: 'Cliente criado com sucesso',
        severity: 'success',
      });
      
      // Limpar formulário
      setNewCliente({ 
        nome: '', 
        ativo: true,
        periodos_acesso: []
      });
      
      // Recarregar dados
      fetchClientes();
    } catch (error) {
      console.error('Erro ao criar cliente:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao criar cliente',
        severity: 'error',
      });
    }
  };

  const handleOpenEditDialog = async (cliente: Cliente) => {
    try {
      // Carrega os detalhes completos do cliente, incluindo períodos
      const clienteDetail = await ClientesService.getClienteDetail(cliente.id);
      setCurrentCliente(cliente);
      setCurrentClienteDetail(clienteDetail);
      
      // Inicializar dados para edição
      setEditCliente({
        nome: cliente.nome,
        descricao: cliente.descricao,
        ativo: cliente.ativo
      });
      
      // Inicializar períodos para edição
      if (clienteDetail.periodos_info) {
        setPeriodosEditing(clienteDetail.periodos_info.map(p => ({
          inicio: p.inicio,
          fim: p.fim,
          marcas_ids: p.marcas_ids || [],
          itens_ids: p.itens_ids || [],
          setores_ids: p.setores_ids || [],
          emissoras_ids: p.emissoras_ids || []
        })));
      } else {
        setPeriodosEditing([]);
      }
      
      setOpenEditDialog(true);
    } catch (error) {
      console.error('Erro ao carregar detalhes do cliente:', error);
      
      // Mesmo com erro, abrir o diálogo com os dados básicos
      setCurrentCliente(cliente);
      // Inicializar apenas com dados básicos
      setEditCliente({
        nome: cliente.nome,
        descricao: cliente.descricao,
        ativo: cliente.ativo
      });
      
      // Inicializar períodos vazios
      setPeriodosEditing([]);
      
      setOpenEditDialog(true);
      
      setSnackbar({
        open: true,
        message: 'Não foi possível carregar períodos do cliente. Apenas os dados básicos estão disponíveis para edição.',
        severity: 'error',
      });
    }
  };

  const handleEditCliente = async () => {
    if (!currentCliente) return;

    try {
      // Verificar campos obrigatórios
      if (editCliente.nome === '') {
        setSnackbar({
          open: true,
          message: 'Nome do cliente é obrigatório',
          severity: 'error',
        });
        return;
      }

      // Incluir periodos_acesso na atualização
      const clienteData: ClienteUpdate = {
        ...editCliente,
        periodos_acesso: periodosEditing
      };

      await ClientesService.updateCliente(currentCliente.id, clienteData);
      setOpenEditDialog(false);
      setSnackbar({
        open: true,
        message: 'Cliente atualizado com sucesso',
        severity: 'success',
      });
      
      // Limpar estados
      setCurrentCliente(null);
      setCurrentClienteDetail(null);
      setEditCliente({});
      setPeriodosEditing([]);
      
      // Recarregar dados
      fetchClientes();
    } catch (error) {
      console.error('Erro ao atualizar cliente:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao atualizar cliente',
        severity: 'error',
      });
    }
  };

  const handleOpenDeleteDialog = (cliente: Cliente) => {
    setCurrentCliente(cliente);
    setOpenDeleteDialog(true);
  };

  const handleDeleteCliente = async () => {
    if (!currentCliente) return;

    try {
      await ClientesService.deleteCliente(currentCliente.id);
      setOpenDeleteDialog(false);
      setSnackbar({
        open: true,
        message: 'Cliente excluído com sucesso',
        severity: 'success',
      });
      
      // Limpar estados
      setCurrentCliente(null);
      
      // Recarregar dados
      fetchClientes();
    } catch (error) {
      console.error('Erro ao excluir cliente:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao excluir cliente',
        severity: 'error',
      });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Função para renderizar o conteúdo da aba de períodos
  const renderPeriodosTab = () => {
    const periodos = openCreateDialog ? newCliente.periodos_acesso : periodosEditing;
    
    return (
      <Box sx={{ mt: 2 }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAddPeriodo}
          sx={{ mb: 2 }}
        >
          Adicionar Período
        </Button>
        
        {periodos.length === 0 ? (
          <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
            Nenhum período de acesso cadastrado
          </Typography>
        ) : (
          periodos.map((periodo, index) => (
            <Accordion key={index} sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ display: 'flex', alignItems: 'center' }}>
                  <DateRangeIcon sx={{ mr: 1 }} />
                  Período {index + 1}: {new Date(periodo.inicio).toLocaleDateString()} - {new Date(periodo.fim).toLocaleDateString()}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                      <DatePicker
                        label="Data Início"
                        value={periodo.inicio ? new Date(periodo.inicio) : null}
                        onChange={(newValue: Date | null) => {
                          if (newValue) {
                            handlePeriodoChange(index, 'inicio', newValue.toISOString().split('T')[0]);
                          }
                        }}
                        renderInput={(props) => <TextField {...props} fullWidth />}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                      <DatePicker
                        label="Data Fim"
                        value={periodo.fim ? new Date(periodo.fim) : null}
                        onChange={(newValue: Date | null) => {
                          if (newValue) {
                            handlePeriodoChange(index, 'fim', newValue.toISOString().split('T')[0]);
                          }
                        }}
                        renderInput={(props) => <TextField {...props} fullWidth />}
                      />
                    </LocalizationProvider>
                  </Grid>
                  
                  {/* Marcas */}
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      options={marcas}
                      getOptionLabel={(option) => option.nome}
                      value={marcas.filter(marca => periodo.marcas_ids.includes(marca.id))}
                      onChange={(_, newValue) => {
                        handleEntitySelectionChange(index, 'marcas_ids', newValue.map(v => v.id));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Marcas"
                          placeholder="Selecione as marcas"
                          fullWidth
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <BookmarkIcon />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            )
                          }}
                        />
                      )}
                    />
                  </Grid>
                  
                  {/* Itens */}
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      options={itens}
                      getOptionLabel={(option) => option.nome}
                      value={itens.filter(item => periodo.itens_ids.includes(item.id))}
                      onChange={(_, newValue) => {
                        handleEntitySelectionChange(index, 'itens_ids', newValue.map(v => v.id));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Itens"
                          placeholder="Selecione os itens"
                          fullWidth
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <BusinessCenterIcon />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            )
                          }}
                        />
                      )}
                    />
                  </Grid>
                  
                  {/* Setores */}
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      options={setores}
                      getOptionLabel={(option) => option.nome}
                      value={setores.filter(setor => periodo.setores_ids.includes(setor.id))}
                      onChange={(_, newValue) => {
                        handleEntitySelectionChange(index, 'setores_ids', newValue.map(v => v.id));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Setores"
                          placeholder="Selecione os setores"
                          fullWidth
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <CategoryIcon />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            )
                          }}
                        />
                      )}
                    />
                  </Grid>
                  
                  {/* Emissoras - Optimized selection */}
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      options={emissoras}
                      getOptionLabel={(option) => option.nome}
                      value={emissoras.filter(emissora => periodo.emissoras_ids.includes(emissora.id))}
                      onChange={(_, newValue) => {
                        handleEntitySelectionChange(index, 'emissoras_ids', newValue.map(v => v.id));
                      }}
                      disableCloseOnSelect
                      limitTags={3}
                      ListboxProps={{
                        style: { maxHeight: '200px' }
                      }}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          <Box component="span" sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            width: '100%',
                            justifyContent: 'space-between'
                          }}>
                            <span>{option.nome}</span>
                            <Chip 
                              size="small" 
                              label={`ID: ${option.id}`} 
                              variant="outlined" 
                              sx={{ ml: 1, fontSize: '0.7rem' }} 
                            />
                          </Box>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Emissoras"
                          placeholder="Busque e selecione as emissoras"
                          fullWidth
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <CampaignIcon />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            )
                          }}
                          helperText={`${periodo.emissoras_ids.length} emissora(s) selecionada(s)`}
                        />
                      )}
                      filterOptions={(options, { inputValue }) => {
                        // Custom filter to improve search experience
                        if (!inputValue) return options;
                        
                        const searchTerms = inputValue.toLowerCase().split(/\s+/);
                        return options.filter(option => {
                          const optionText = option.nome.toLowerCase();
                          return searchTerms.every(term => optionText.includes(term));
                        });
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleRemovePeriodo(index)}
                    >
                      Remover Período
                    </Button>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </Box>
    );
  };

  return (
    <SidebarLayout>
      <Box p={3}>
        <Typography variant="h4" gutterBottom display="flex" alignItems="center">
          <PeopleIcon sx={{ mr: 2 }} />
          Gerenciamento de Clientes
        </Typography>
        
        {/* Área de filtros */}
        <Paper 
          elevation={2} 
          sx={{ p: 2, mb: 3, backgroundColor: theme.palette.background.default }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Buscar por nome"
                variant="outlined"
                size="small"
                value={filter.nome || ''}
                onChange={(e) => handleFilterChange('nome', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: filter.nome ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={clearSearchFilter}
                        edge="end"
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="ativo-filter-label">Status</InputLabel>
                <Select
                  labelId="ativo-filter-label"
                  label="Status"
                  name="ativo"
                  value={filter.ativo === undefined ? '' : filter.ativo ? 'true' : 'false'}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleFilterChange('ativo', value === '' ? undefined : value === 'true');
                  }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="true">Ativos</MenuItem>
                  <MenuItem value="false">Inativos</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenCreateDialog(true)}
                fullWidth
              >
                Novo Cliente
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Tabela de Clientes */}
        <Paper elevation={3} sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 280px)' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Nome</TableCell>
                  <TableCell>Descrição</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Períodos</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : clientes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Nenhum cliente encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  clientes.map((cliente) => (
                    <TableRow key={cliente.id} hover>
                      <TableCell>{cliente.id}</TableCell>
                      <TableCell>{cliente.nome}</TableCell>
                      <TableCell>{cliente.descricao || '-'}</TableCell>
                      <TableCell>
                        <Chip 
                          label={cliente.ativo ? 'Ativo' : 'Inativo'} 
                          color={cliente.ativo ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {cliente.related_periodos && cliente.related_periodos.length > 0 ? (
                          <Chip 
                            label={`${cliente.related_periodos.length} período(s)`}
                            color="primary"
                            size="small"
                          />
                        ) : (
                          <Chip 
                            label="Sem períodos"
                            variant="outlined"
                            size="small"
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenEditDialog(cliente)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleOpenDeleteDialog(cliente)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Linhas por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </Paper>
      </Box>

      {/* Modal de Criação */}
      <Dialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Novo Cliente</DialogTitle>
        <DialogContent>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
          >
            <Tab label="Dados Básicos" />
            <Tab label="Períodos de Acesso" />
          </Tabs>
          
          {/* Aba de dados básicos */}
          {tabValue === 0 && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nome"
                  name="nome"
                  required
                  value={newCliente.nome || ''}
                  onChange={(e) => setNewCliente({ ...newCliente, nome: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Descrição"
                  name="descricao"
                  multiline
                  rows={3}
                  value={newCliente.descricao || ''}
                  onChange={(e) => setNewCliente({ ...newCliente, descricao: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={newCliente.ativo === undefined ? true : newCliente.ativo}
                      onChange={(e) => setNewCliente({ ...newCliente, ativo: e.target.checked })}
                      name="ativo"
                    />
                  }
                  label="Cliente ativo"
                />
              </Grid>
            </Grid>
          )}
          
          {/* Aba de períodos */}
          {tabValue === 1 && renderPeriodosTab()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleCreateCliente}
            disabled={!newCliente.nome}
          >
            Criar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Edição */}
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Editar Cliente</DialogTitle>
        <DialogContent>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
          >
            <Tab label="Dados Básicos" />
            <Tab label="Períodos de Acesso" />
          </Tabs>
          
          {/* Aba de dados básicos */}
          {tabValue === 0 && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nome"
                  name="nome"
                  required
                  value={editCliente.nome || ''}
                  onChange={(e) => setEditCliente({ ...editCliente, nome: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Descrição"
                  name="descricao"
                  multiline
                  rows={3}
                  value={editCliente.descricao || ''}
                  onChange={(e) => setEditCliente({ ...editCliente, descricao: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editCliente.ativo === undefined ? 
                        (currentCliente ? currentCliente.ativo : true) : 
                        editCliente.ativo}
                      onChange={(e) => setEditCliente({ ...editCliente, ativo: e.target.checked })}
                      name="ativo"
                    />
                  }
                  label="Cliente ativo"
                />
              </Grid>
            </Grid>
          )}
          
          {/* Aba de períodos */}
          {tabValue === 1 && renderPeriodosTab()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleEditCliente}
            disabled={!editCliente.nome}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Confirmação de Exclusão */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir o cliente "{currentCliente?.nome}"?
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleDeleteCliente}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para mensagens */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </SidebarLayout>
  );
};

export default ClientesPage; 